import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

// Mouse direction, not a dwell timer, protects entry into the open submenu.
const DESKTOP_HOVER = '(min-width: 990px) and (hover: hover) and (pointer: fine)';
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    const options = { passive: true, signal: this.#abortController.signal };
    this.addEventListener('pointerenter', this.#trackPointer, options);
    this.addEventListener('pointermove', this.#trackPointer, options);
    this.addEventListener('pointerleave', this.#resetPointerIntent, options);
    window.addEventListener('resize', this.#resetPointerIntent, options);
    window.addEventListener('scroll', this.#resetPointerIntent, options);

    /*
     * Djurslottet 2026-08-23 (prestanda).
     *
     * Har lag tidigare onDocumentLoaded(this.#preloadImages), som kors nar hela
     * sidan laddat klart. #preloadImages tar bort loading="lazy" fran menyns
     * bilder - och att ta bort attributet fran en bild som annu inte hamtats far
     * webblasaren att borja hamta den DIREKT.
     *
     * Uppmatt pa Om oss-sidan: 47 osynliga megamenybilder, cirka 1,5 MB och 42
     * extra anrop, startade vid varje sidvisning och konkurrerade med sidans
     * eget innehall. Menyn var stangd hela tiden.
     *
     * Nu varmas bilderna forst nar besokaren narmar sig menyn - vid forsta
     * pointerenter eller focusin. Da hinner de anda ligga i cachen innan
     * megamenyn hunnit oppna sig, utan att kosta nagot pa sidor dar menyn
     * aldrig ror vid.
     */
    this.addEventListener('pointerenter', this.#preloadImages, {
      once: true,
      signal: this.#abortController.signal,
    });
    this.addEventListener('focusin', this.#preloadImages, {
      once: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resetPointerIntent();
    this.#debouncedDeactivate.cancel();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Open immediately on purposeful movement; protect diagonal entry and fast passes.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#cancelPendingActivation();

    const desktopMouse =
      event.type === 'pointerenter' &&
      event.pointerType === 'mouse' &&
      window.matchMedia(DESKTOP_HOVER).matches;

    if (!desktopMouse) {
      this.#resetPointerIntent();
      this.#activateHandler(event);
      return;
    }

    // pointerenter is delivered before the corresponding pointermove.
    this.#trackPointer(event);
    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) {
      this.#aim = null;
      return;
    }

    const recent = this.#pointer && event.timeStamp - this.#pointer.time < 100;
    if (recent && this.#motion && !this.#protectsPanel(event) && !this.#isFastPass()) {
      this.#activateHandler(event);
      return;
    }

    // Only ambiguous motion waits for the pointer to settle, for two paint frames.
    // There is no fixed opening/switching delay and no page-wide mouse listener.
    this.#pendingActivation = event;
    this.#settleVersion = this.#motionVersion;
    this.#intentFrame = requestAnimationFrame(this.#settlePointer);
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#cancelPendingActivation();
    this.#aim = null;

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #pointer = null;
  #motion = null;
  #aim = null;
  #pendingActivation = null;
  #intentFrame = 0;
  #motionVersion = 0;
  #settleVersion = 0;
  #quietFrames = 0;

  #cancelPendingActivation = () => {
    cancelAnimationFrame(this.#intentFrame);
    this.#intentFrame = 0;
    this.#pendingActivation = null;
    this.#quietFrames = 0;
  };

  #resetPointerIntent = () => {
    this.#cancelPendingActivation();
    this.#pointer = null;
    this.#motion = null;
    this.#aim = null;
  };

  #isFastPass = () => {
    const motion = this.#motion;
    return motion && Math.abs(motion.dx) > Math.max(3, Math.abs(motion.dy) * 2.5) &&
      Math.abs(motion.dx) / motion.dt > 0.35;
  };

  #protectsPanel = (point) => {
    const aim = this.#aim;
    if (!aim || point.clientY <= aim.y + 1 || point.clientY > aim.bottom) return false;
    const progress = (point.clientY - aim.y) / (aim.bottom - aim.y);
    const left = aim.x + (aim.left - aim.x) * progress;
    const right = aim.x + (aim.right - aim.x) * progress;
    return point.clientX >= left && point.clientX <= right;
  };

  #trackPointer = (event) => {
    if (event.pointerType !== 'mouse' || !window.matchMedia(DESKTOP_HOVER).matches) return;
    const previous = this.#pointer;
    const point = { x: event.clientX, y: event.clientY, time: event.timeStamp };
    if (previous) {
      const dx = point.x - previous.x;
      const dy = point.y - previous.y;
      // Ignore sub-pixel/trackpad jitter instead of keeping a candidate pending.
      if (Math.abs(dx) + Math.abs(dy) < 2) return;
      this.#motion = point.time - previous.time < 100 ?
        { dx, dy, dt: Math.max(1, point.time - previous.time) } : null;
    }
    this.#pointer = point;
    this.#motionVersion++;

    const pending = this.#pendingActivation;
    if (!pending) return;
    if (!pending.target.matches(':hover')) {
      this.#cancelPendingActivation();
      return;
    }
    // A reversal/upward move expresses a new intention; don't hold the old panel.
    if (this.#motion?.dy < -1) this.#aim = null;
    if (this.#motion && !this.#protectsPanel(event) && !this.#isFastPass()) {
      this.#activateHandler(pending);
    }
  };

  #settlePointer = () => {
    this.#intentFrame = 0;
    const pending = this.#pendingActivation;
    if (!pending || !pending.target.matches(':hover')) {
      this.#cancelPendingActivation();
      return;
    }
    this.#quietFrames = this.#settleVersion === this.#motionVersion ? this.#quietFrames + 1 : 0;
    this.#settleVersion = this.#motionVersion;
    if (this.#quietFrames >= 2) this.#activateHandler(pending);
    else this.#intentFrame = requestAnimationFrame(this.#settlePointer);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#cancelPendingActivation();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Leaving a pending sibling must also close the old menu if the pointer
    // exits navigation. Entering any menu/panel cancels this grace timer.
    const leavingDesktopMouse =
      event.type === 'pointerleave' &&
      event.pointerType === 'mouse' &&
      window.matchMedia(DESKTOP_HOVER).matches;

    if (leavingDesktopMouse && item === this.#state.activeItem) {
      const panel = this.dataset.overflowExpanded === 'true' ? this.overflowMenu : findSubmenu(item);
      const bounds = panel?.getBoundingClientRect();
      const recent = this.#pointer && event.timeStamp - this.#pointer.time < 100;
      const origin = recent ? this.#pointer : { x: event.clientX, y: event.clientY };
      // Read layout once at the heading boundary, never on pointermove/frames.
      this.#aim = bounds?.height && origin.y < bounds.top + 12 ? {
        x: origin.x, y: origin.y, left: bounds.left - 12,
        right: bounds.right + 12, bottom: bounds.top + 12,
      } : null;
    }

    if (item === this.#state.activeItem || leavingDesktopMouse) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;
    this.#resetPointerIntent();

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
